import { useDafTracking } from "@/canvas/features/FormV2/hooks/useDafTracking";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { DafProviderField } from "./DafProviderField/DafProviderField";
import { OfflinePaymentMethod } from "./OfflinePaymentMethod/OfflinePaymentMethod";
import { FC, RefObject, useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  getStripeCustomPaymentMethodIdFromCountry,
  isBankDebitPaymentMethod,
  isStripePaymentMethod,
  OrganizationCountry,
  countryHasAch,
  PAYMENT_METHOD_FROM_STRIPE,
  PaymentMethod,
} from "@simplyk/common";
import { PaymentElement as StripePaymentElement, useElements } from "@stripe/react-stripe-js";
import { LayoutObject, StripePaymentElementChangeEvent, StripePaymentElementOptions } from "@stripe/stripe-js";

import { PaymentErrorReport } from "./helper";
import { usePaymentErrorContext } from "./PaymentErrorContext";
import { StripeElementErrorBoundary } from "./StripeElementErrorBoundary/StripeElementErrorBoundary";

import { StripeContext } from "@/common/contexts/StripeContext";
import { isPaymentMethodWithStripe, STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD } from "@/common/helpers/stripe";
import { useTranslate } from "@/common/hooks/useTranslate";
import { PAYPAL_CUSTOM_PAYMENT_METHOD_IDS, VENMO_CUSTOM_PAYMENT_METHOD_IDS } from "@/common/providers/StripeProvider";
import {
  AchFormPaymentMethod,
  CreditCardFormPaymentMethod,
  PadFormPaymentMethod,
} from "@/forms-design-system/FormPaymentMethod";
import { CashFormPaymentMethod } from "@/forms-design-system/FormPaymentMethod/CashFormPaymentMethod";
import { FormChip } from "@/forms-design-system/FormChip";
import { FormInfobox } from "@/forms-design-system/FormInfobox";
import { BaseFormPaymentMethod } from "@/forms-design-system/FormPaymentMethod/BaseFormPaymentMethod";
import { getStripeAppearance } from "@/forms-design-system/FormPaymentMethod/FormPaymentMethodTokens";
import { StripeBank } from "@/icons/external";
import { Warning } from "@/icons/outlined";

export interface PaymentMethodOptionsProps {
  offlinePaymentWording?: string | null;
  hasPad: boolean;
  hasAch: boolean;
  hasWalletsBelow?: boolean;
  stripePaymentMethods: PaymentMethod[];
  zeffyPaymentMethods: PaymentMethod[];
  organizationName: string;
  organizationCountry: OrganizationCountry;
  prioritizeBankPayment: boolean;
  collapseRecommendedBankPayment?: boolean;
  setSelectedPaymentMethod: (method: PaymentMethod | undefined, complete?: boolean) => void;
  selectedPaymentMethod: PaymentMethod | undefined;
  chequeDescription: string | null | undefined;
  displayExpressCheckout: boolean;
  expressCheckoutIsSelected: boolean;
  onClearPaymentError: () => void;
  onPaymentError?: (report: PaymentErrorReport) => void;
  autoSelectedBankPaymentMethodRef?: RefObject<PaymentMethod | undefined>;
}

export const PaymentMethodOptions: FC<PaymentMethodOptionsProps> = ({
  offlinePaymentWording,
  hasPad,
  hasAch,
  hasWalletsBelow = false,
  stripePaymentMethods,
  zeffyPaymentMethods,
  organizationName,
  organizationCountry,
  prioritizeBankPayment,
  collapseRecommendedBankPayment = false,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  chequeDescription,
  displayExpressCheckout,
  expressCheckoutIsSelected,
  onClearPaymentError,
  onPaymentError,
  autoSelectedBankPaymentMethodRef,
}) => {
  const { t } = useTranslate();
  const { trackDaf, dafExposureAmount } = useDafTracking();
  const { currentStep, isPreview, form } = useFormV2Context();
  const paymentOptionsRef = useRef<HTMLDivElement>(null);
  const hasDaf = zeffyPaymentMethods.includes(PaymentMethod.Daf);
  // Preloaded payment elements do not count as exposure until the option enters the visible payment step.
  useEffect(() => {
    if (!hasDaf || currentStep !== "payment" || isPreview || !dafExposureAmount || dafExposureAmount.current !== null) {
      return;
    }
    const option = paymentOptionsRef.current?.querySelector('[data-test="payment-method-daf"]');
    if (!option) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && dafExposureAmount.current === null) {
        const values = form.getValues();
        dafExposureAmount.current = "amount" in values ? values.amount : 0;
        trackDaf("DAF Payment Option Viewed");
        observer.disconnect();
      }
    });
    observer.observe(option);
    return () => observer.disconnect();
  }, [currentStep, isPreview, hasDaf, dafExposureAmount, form, trackDaf]);
  const theme = useTheme();
  const elements = useElements();
  const clearPaymentError = onClearPaymentError;
  const { handlePaymentError } = usePaymentErrorContext();
  const reportPaymentError = onPaymentError ?? handlePaymentError;
  const { stripeLoadError, setReadyPaymentElement } = useContext(StripeContext);
  const reportedStripeLoadError = useRef<Error | null>(null);

  // Stripe.js failures happen outside Elements and must reach the same visible error banner.
  useEffect(() => {
    if (!stripeLoadError || reportedStripeLoadError.current === stripeLoadError) {
      return;
    }
    reportedStripeLoadError.current = stripeLoadError;
    reportPaymentError({
      rawErrorCode: "stripe_initialization_failed",
      rawErrorMessage: stripeLoadError.message,
      step: "stripe-load",
      paymentMethod: selectedPaymentMethod,
      isExpressCheckout: false,
      isKnownError: true,
    });
  }, [stripeLoadError, reportPaymentError, selectedPaymentMethod]);

  const paypalCustomPaymentMethodId = getStripeCustomPaymentMethodIdFromCountry(
    organizationCountry,
    PAYPAL_CUSTOM_PAYMENT_METHOD_IDS
  );
  const venmoCustomPaymentMethodId = getStripeCustomPaymentMethodIdFromCountry(
    organizationCountry,
    VENMO_CUSTOM_PAYMENT_METHOD_IDS
  );
  const hasStripePaymentMethods = stripePaymentMethods.length > 0;
  const hasSepa = stripePaymentMethods.includes(PaymentMethod.Sepa);
  const showCustomAccordion = stripePaymentMethods.length === 1 && zeffyPaymentMethods.length > 0;
  const initialSelectedPaymentMethod = useRef(selectedPaymentMethod);
  const initialStripePaymentMethod =
    initialSelectedPaymentMethod.current && isPaymentMethodWithStripe(initialSelectedPaymentMethod.current)
      ? STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD[initialSelectedPaymentMethod.current]
      : undefined;
  const paymentMethodOrder = useMemo(
    () =>
      (prioritizeBankPayment
        ? [PaymentMethod.Ach, PaymentMethod.Sepa, PaymentMethod.Card, PaymentMethod.CashApp]
        : [PaymentMethod.Card, PaymentMethod.Ach, PaymentMethod.Sepa, PaymentMethod.CashApp]
      )
        .filter(isPaymentMethodWithStripe)
        .filter((method) => stripePaymentMethods.includes(method))
        .map((method) => STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD[method]),
    [prioritizeBankPayment, stripePaymentMethods]
  );

  const collapseStripePaymentElement = useCallback(() => {
    elements?.getElement("payment")?.collapse();
  }, [elements]);

  const ownAutoSelectedBankPaymentMethodRef = useRef<PaymentMethod | undefined>(undefined);
  const autoSelectedBankPaymentMethod = autoSelectedBankPaymentMethodRef ?? ownAutoSelectedBankPaymentMethodRef;
  const previousBankPriorityRef = useRef<{ prioritizeBankPayment: boolean; hasAch: boolean; hasPad: boolean } | null>(
    null
  );

  useEffect(() => {
    const previousBankPriority = previousBankPriorityRef.current;
    previousBankPriorityRef.current = { prioritizeBankPayment, hasAch, hasPad };
    const isOnlyCollapseChange =
      previousBankPriority?.prioritizeBankPayment === prioritizeBankPayment &&
      previousBankPriority.hasAch === hasAch &&
      previousBankPriority.hasPad === hasPad;

    if (isOnlyCollapseChange) {
      const isBankAutoSelected =
        Boolean(autoSelectedBankPaymentMethod.current) &&
        selectedPaymentMethod === autoSelectedBankPaymentMethod.current;
      if (collapseRecommendedBankPayment && isBankAutoSelected) {
        autoSelectedBankPaymentMethod.current = undefined;
        setSelectedPaymentMethod(undefined);
        collapseStripePaymentElement();
      }
      return;
    }

    if (prioritizeBankPayment && selectedPaymentMethod !== PaymentMethod.Daf) {
      if (collapseRecommendedBankPayment) {
        const isBankChosenByDonor =
          isBankDebitPaymentMethod(selectedPaymentMethod) &&
          selectedPaymentMethod !== autoSelectedBankPaymentMethod.current;
        autoSelectedBankPaymentMethod.current = undefined;
        if (!isBankChosenByDonor) {
          setSelectedPaymentMethod(undefined);
          collapseStripePaymentElement();
        }
        return;
      }

      if (hasAch) {
        if (selectedPaymentMethod !== PaymentMethod.Ach) {
          autoSelectedBankPaymentMethod.current = PaymentMethod.Ach;
        }
        setSelectedPaymentMethod(PaymentMethod.Ach);
      }

      if (hasPad) {
        if (selectedPaymentMethod !== PaymentMethod.Pad) {
          autoSelectedBankPaymentMethod.current = PaymentMethod.Pad;
        }
        setSelectedPaymentMethod(PaymentMethod.Pad);
        collapseStripePaymentElement();
      }
      return;
    }

    const bankPaymentMethodToReset = autoSelectedBankPaymentMethod.current;
    autoSelectedBankPaymentMethod.current = undefined;
    if (!bankPaymentMethodToReset || selectedPaymentMethod !== bankPaymentMethodToReset) {
      return;
    }
    if (displayExpressCheckout) {
      setSelectedPaymentMethod(undefined);
    } else if (stripePaymentMethods.includes(PaymentMethod.Card)) {
      setSelectedPaymentMethod(PaymentMethod.Card);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritizeBankPayment, hasAch, hasPad, collapseRecommendedBankPayment]);

  // We should collapse the stripe payment elements when express checkout is selected
  useEffect(() => {
    if (displayExpressCheckout && expressCheckoutIsSelected) {
      elements?.getElement("payment")?.collapse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayExpressCheckout, expressCheckoutIsSelected]);

  const displayPadOnTop = prioritizeBankPayment && hasPad;
  useEffect(() => {
    if (stripePaymentMethods.length > 0) {
      const appearance = getStripeAppearance(theme, {
        hasMoreItemsAbove: displayPadOnTop,
        hasMoreItemsBelow: zeffyPaymentMethods.length > 0 || hasWalletsBelow,
      });
      elements?.update({
        appearance,
      });
    }
  }, [
    displayPadOnTop,
    hasWalletsBelow,
    elements,
    selectedPaymentMethod,
    stripePaymentMethods,
    stripePaymentMethods.length,
    theme,
    theme.palette.text.brand.moderate,
    zeffyPaymentMethods,
    zeffyPaymentMethods.length,
  ]);

  const handleZeffyPaymentMethodChange = useCallback(
    (value: PaymentMethod) => {
      if (value === PaymentMethod.Daf) {
        trackDaf("DAF Payment Method Selected");
      }
      clearPaymentError();
      collapseStripePaymentElement();
      autoSelectedBankPaymentMethod.current = undefined;
      setSelectedPaymentMethod(value);
    },
    [collapseStripePaymentElement, clearPaymentError, setSelectedPaymentMethod, trackDaf, autoSelectedBankPaymentMethod]
  );

  const handlePaypalSelection = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      if (paypalCustomPaymentMethodId && event.value.type === paypalCustomPaymentMethodId) {
        setSelectedPaymentMethod(PaymentMethod.PayPal);
        clearPaymentError();
        return;
      }
      if (venmoCustomPaymentMethodId && event.value.type === venmoCustomPaymentMethodId) {
        setSelectedPaymentMethod(PaymentMethod.Venmo);
        clearPaymentError();
      }
    },
    [clearPaymentError, setSelectedPaymentMethod, paypalCustomPaymentMethodId, venmoCustomPaymentMethodId]
  );

  const handleStripePaymentMethodChange = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      if (event.collapsed) {
        return;
      }
      handlePaypalSelection(event);

      // Ensure that only Stripe methods are handled here
      if (!isStripePaymentMethod(event.value.type)) {
        return;
      }

      const paymentMethod = PAYMENT_METHOD_FROM_STRIPE[event.value.type];
      if (showCustomAccordion && paymentMethod !== selectedPaymentMethod) {
        return;
      }
      clearPaymentError();
      if (paymentMethod !== selectedPaymentMethod || event.complete) {
        autoSelectedBankPaymentMethod.current = undefined;
      }
      setSelectedPaymentMethod(paymentMethod, event.complete);
    },
    [
      clearPaymentError,
      setSelectedPaymentMethod,
      handlePaypalSelection,
      selectedPaymentMethod,
      showCustomAccordion,
      autoSelectedBankPaymentMethod,
    ]
  );

  const handlePaymentElementReady = useCallback(() => {
    const paymentElement = elements?.getElement("payment");
    setReadyPaymentElement(paymentElement ?? null);
    paymentElement?.update({ paymentMethodOrder });
    if (initialSelectedPaymentMethod.current === PaymentMethod.Daf) {
      paymentElement?.collapse();
    }
  }, [elements, paymentMethodOrder, setReadyPaymentElement]);

  const paymentMethodOptions: StripePaymentElementOptions = useMemo(
    () => ({
      business: { name: organizationName },
      fields: {
        billingDetails: {
          name: "never",
          email: "never",
          address: "never",
        },
      },
      wallets: { googlePay: "never", applePay: "never" },
      layout: {
        type: showCustomAccordion ? "tabs" : "accordion",
        defaultCollapsed:
          selectedPaymentMethod === PaymentMethod.Daf ||
          collapseRecommendedBankPayment ||
          (displayExpressCheckout && !prioritizeBankPayment && selectedPaymentMethod !== PaymentMethod.Sepa),
        ...(!showCustomAccordion && { radios: true }), // radios is only available for accordion layout
      } as LayoutObject,
      paymentMethodOrder:
        hasSepa && initialStripePaymentMethod
          ? [
              initialStripePaymentMethod,
              ...paymentMethodOrder.filter((method) => method !== initialStripePaymentMethod),
            ].filter((method) => paymentMethodOrder.includes(method))
          : paymentMethodOrder,
    }),
    [
      organizationName,
      showCustomAccordion,
      displayExpressCheckout,
      prioritizeBankPayment,
      collapseRecommendedBankPayment,
      selectedPaymentMethod,
      hasSepa,
      initialStripePaymentMethod,
      paymentMethodOrder,
    ]
  );

  const stripePaymentElements = hasStripePaymentMethods && (
    <StripeElementErrorBoundary
      key={prioritizeBankPayment ? "prioritizeBankPayment" : "cardFirst"}
      fallback={
        <FormInfobox
          icon={<Warning />}
          variant="error"
          description={t("common", "payment.elementUnavailable")}
          data-test="payment-element-unavailable"
        />
      }
    >
      <StripePaymentElement
        id="payment-element"
        onReady={handlePaymentElementReady}
        onLoadError={({ error }) => {
          setReadyPaymentElement(null);
          reportPaymentError({
            rawErrorCode: "stripe_payment_element_load_error",
            rawErrorMessage: error.message || t("common", "unableToLoadStripe"),
            step: "stripe-load",
            paymentMethod: selectedPaymentMethod,
            isExpressCheckout: false,
            isKnownError: true,
          });
        }}
        onChange={handleStripePaymentMethodChange}
        options={paymentMethodOptions}
      />
    </StripeElementErrorBoundary>
  );

  const filteredZeffyPayMethods = useMemo(() => {
    // filter() already returns a copy, and toSorted() crashes donors on browsers older than Chrome 110 / Safari 16.
    return zeffyPaymentMethods
      .filter((method) => !displayPadOnTop || method !== PaymentMethod.Pad)
      .sort((left, right) => Number(left === PaymentMethod.Daf) - Number(right === PaymentMethod.Daf));
  }, [displayPadOnTop, zeffyPaymentMethods]);

  const renderPaymentMethod = ({
    paymentMethod,
    firstItem,
    lastItem,
    children,
  }: {
    paymentMethod: PaymentMethod;
    firstItem: boolean;
    lastItem: boolean;
    children?: React.ReactNode;
  }) => {
    switch (paymentMethod) {
      case PaymentMethod.Daf:
        return (
          <OfflinePaymentMethod
            key={paymentMethod}
            paymentMethod={PaymentMethod.Daf}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test="payment-method-daf"
          >
            <DafProviderField />
          </OfflinePaymentMethod>
        );
      case PaymentMethod.Cheque:
        return (
          <OfflinePaymentMethod
            key={paymentMethod}
            paymentMethod={PaymentMethod.Cheque}
            firstItem={firstItem}
            lastItem={lastItem}
            checked={selectedPaymentMethod === paymentMethod}
            customMessage={chequeDescription ?? undefined}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test="payment-method-cheque"
          />
        );
      case PaymentMethod.Pad:
        return (
          <PadFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test="payment-method-pad"
          />
        );
      case PaymentMethod.Ach:
        return (
          <AchFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test={paymentMethod.toLowerCase()}
          >
            {children}
          </AchFormPaymentMethod>
        );
      case PaymentMethod.Sepa:
        return (
          <BaseFormPaymentMethod
            key={paymentMethod}
            icon={<StripeBank />}
            label={t("common", "paymentMethod.sepa")}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test="payment-method-sepa"
          >
            {children}
          </BaseFormPaymentMethod>
        );
      case PaymentMethod.Card:
        return (
          <CreditCardFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test={paymentMethod.toLowerCase()}
          >
            {children}
          </CreditCardFormPaymentMethod>
        );

      case PaymentMethod.Cash:
        return (
          <CashFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            label={offlinePaymentWording ?? undefined}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test={paymentMethod.toLowerCase()}
          />
        );

      default:
        throw new Error(`Unsupported payment method: ${paymentMethod}`);
    }
  };

  const showBankRecommendation = countryHasAch(organizationCountry) && prioritizeBankPayment && hasAch;

  return (
    <Box
      ref={paymentOptionsRef}
      data-test="payment-elements-wrapper"
      sx={{ position: "relative", paddingTop: showBankRecommendation ? 1.5 : 0 }}
    >
      {showBankRecommendation && (
        <FormChip
          label={t("common", "payment.recommended")}
          vibe="positive"
          data-test="bank-payment-recommended"
          sx={{ position: "absolute", top: 0, right: (theme) => theme.spacing(2), zIndex: 1, pointerEvents: "none" }}
        />
      )}
      {displayPadOnTop &&
        renderPaymentMethod({
          paymentMethod: PaymentMethod.Pad,
          firstItem: true,
          lastItem: !hasStripePaymentMethods && filteredZeffyPayMethods.length === 0 && !hasWalletsBelow,
        })}
      {showCustomAccordion
        ? renderPaymentMethod({
            paymentMethod: stripePaymentMethods[0],
            firstItem: !displayPadOnTop,
            lastItem: filteredZeffyPayMethods.length === 0 && !hasWalletsBelow,
            children: stripePaymentElements,
          })
        : stripePaymentElements}

      {filteredZeffyPayMethods.length > 0 && (
        <Box
          sx={{
            ...(stripePaymentMethods.length > 1 && {
              marginTop: "-5px",
            }),
          }}
        >
          {filteredZeffyPayMethods.map((paymentMethod, index) =>
            renderPaymentMethod({
              paymentMethod,
              firstItem: !stripePaymentMethods.length && !displayPadOnTop,
              lastItem: index + 1 === filteredZeffyPayMethods.length && !hasWalletsBelow,
            })
          )}
        </Box>
      )}
    </Box>
  );
};
